// Home.js
import React, { useState, useEffect, useRef } from 'react';
import './Home.css';

// Import your quartzite images - update paths according to your folder structure
import quartzite1 from '../Assets/1.png';
import quartzite2 from '../Assets/2.png';
import quartzite3 from '../Assets/3.png';
import quartzite4 from '../Assets/4.png';
import quartzite5 from '../Assets/5.png';
import quartzite6 from '../Assets/6.png';

// Import interior design images
import interior1 from '../Assets/i1.jpeg';
import interior2 from '../Assets/i2.jpeg';
import interior3 from '../Assets/i3.jpeg';

// Import new showcase images
import elevatorLobby from '../Assets/elevator-lobby.jpg';
import quarryBlock from '../Assets/quarry-block.jpeg';

// Import slab gallery images
import slab1 from '../Assets/slab1.jpg';
import slab2 from '../Assets/slab2.jpg';
import slab3 from '../Assets/slab3.jpg';

const Home = ({ language = 'en' }) => {
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeFinish, setActiveFinish] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const heroRef = useRef(null);
  const specsRef = useRef(null);
  const [specsInView, setSpecsInView] = useState(false);

  // Translations
  const translations = {
    en: {
      introTitle1: 'INTRODUCING FINEST GRADE OF',
      introTitle2: 'NATURAL QUARTZITE',
      subtitle: 'Premium Beige Quartzite from Our Own Quarry',
      scrollText: 'SCROLL TO EXPLORE',
      whoWeAre: 'WHO WE ARE',
      ownQuarry: 'Own Quarry: Natural Beige Quartzite',
      theRock: 'The Rock',
      theRockText: 'Classified as orthoquartzite rock, Raisen Terra Linea and Raisen Terra Save have glass-like chemical properties with more than 96% silica composition, making them weather and chemical resistant.',
      colorTexture: 'Color & Texture',
      colorTextureText: 'A unique texture with beige background supported by light-gray/brown lines provides a beautiful wood-like appearance and excellent presentation when polished.',
      application: 'Application',
      applicationText: 'Slip-resistant, weather-resistant & anti-acidic properties make our stone ideal for outdoor applications and architectural cladding purposes.',
      performance: 'PERFORMANCE',
      technicalExcellence: 'Technical Excellence',
      whatWeOffer: 'WHAT WE OFFER',
      oneStone: 'One Stone That Does It All',
      offerDescription: 'Our weather-proof stone offers an all-purpose solution with a range of finishes to suit every project. It provides a polished look superior to marble, delivering a stunning sandstone-like appearance for facades.',
      explorefinishes: 'Explore Premium Finishes',
      excellence: 'EXCELLENCE',
      crafted: 'Crafted in Stone.',
      defined: 'Defined by Design.',
      discover: 'Discover Our Creation',
      quartziteBorghese: 'QUARTZITE BORGHESE',
      surfaceStories: 'Surface Stories | Raw • Real • Refined',
      viewGallery: 'View Slab Gallery'
    },
    zh: {
      introTitle1: '介绍最优质的',
      introTitle2: '天然石英岩',
      subtitle: '来自我们自己采石场的优质米色石英岩',
      scrollText: '滚动探索',
      whoWeAre: '我们是谁',
      ownQuarry: '自有采石场：天然米色石英岩',
      theRock: '岩石',
      theRockText: '被归类为正石英岩，Raisen Terra Linea 和 Raisen Terra Save 具有类似玻璃的化学性质，二氧化硅含量超过96%，使其具有耐候和耐化学性。',
      colorTexture: '颜色和纹理',
      colorTextureText: '独特的纹理，米色背景配以浅灰色/棕色线条，抛光后呈现美丽的木纹外观和出色的效果。',
      application: '应用',
      applicationText: '防滑、耐候和抗酸性能使我们的石材成为户外应用和建筑覆层的理想选择。',
      performance: '性能',
      technicalExcellence: '技术卓越',
      whatWeOffer: '我们提供什么',
      oneStone: '一石多用',
      offerDescription: '我们的防风雨石材提供全方位解决方案，配有各种饰面以适应每个项目。它提供优于大理石的抛光外观，为外墙提供令人惊叹的砂岩般的外观。',
      explorefinishes: '探索优质饰面',
      excellence: '卓越',
      crafted: '石材工艺。',
      defined: '设计定义。',
      discover: '发现我们的创造',
      quartziteBorghese: '石英岩博格塞',
      surfaceStories: '表面故事 | 原始 • 真实 • 精致',
      viewGallery: '查看板材画廊'
    },
    it: {
      introTitle1: 'PRESENTAZIONE DEL GRADO PIÙ FINE DI',
      introTitle2: 'QUARZITE NATURALE',
      subtitle: 'Quarzite Beige Premium dalla Nostra Cava',
      scrollText: 'SCORRI PER ESPLORARE',
      whoWeAre: 'CHI SIAMO',
      ownQuarry: 'Cava Propria: Quarzite Naturale Beige',
      theRock: 'La Roccia',
      theRockText: 'Classificata come roccia ortoquarzite, Raisen Terra Linea e Raisen Terra Save hanno proprietà chimiche simili al vetro con oltre il 96% di composizione di silice, rendendole resistenti agli agenti atmosferici e chimici.',
      colorTexture: 'Colore e Texture',
      colorTextureText: 'Una texture unica con sfondo beige supportato da linee grigio chiaro/marroni fornisce un bellissimo aspetto simile al legno e un\'eccellente presentazione quando lucidato.',
      application: 'Applicazione',
      applicationText: 'Le proprietà antiscivolo, resistenti agli agenti atmosferici e antiacide rendono la nostra pietra ideale per applicazioni esterne e scopi di rivestimento architettonico.',
      performance: 'PRESTAZIONI',
      technicalExcellence: 'Eccellenza Tecnica',
      whatWeOffer: 'COSA OFFRIAMO',
      oneStone: 'Una Pietra Che Fa Tutto',
      offerDescription: 'La nostra pietra resistente agli agenti atmosferici offre una soluzione multiuso con una gamma di finiture per adattarsi a ogni progetto. Fornisce un aspetto lucido superiore al marmo, offrendo uno splendido aspetto simile all\'arenaria per le facciate.',
      explorefinishes: 'Esplora Finiture Premium',
      excellence: 'ECCELLENZA',
      crafted: 'Creato nella Pietra.',
      defined: 'Definito dal Design.',
      discover: 'Scopri la Nostra Creazione',
      quartziteBorghese: 'QUARZITE BORGHESE',
      surfaceStories: 'Storie di Superficie | Grezzo • Reale • Raffinato',
      viewGallery: 'Vedi Galleria Lastre'
    }
  };

  const t = translations[language] || translations.en;

  const specs = [
    { label: 'Compressive Strength', value: '98.82', unit: 'MPa' },
    { label: 'Water Absorption', value: '1.6', unit: '%' },
    { label: 'Density', value: '2471', unit: 'kg/m³' },
    { label: 'SiO₂ Content', value: '96+', unit: '%' }
  ];

  const slabGalleryImages = [
    { id: 1, src: slab1, title: 'Premium Slab Collection' },
    { id: 2, src: slab2, title: 'Elegant Veining Pattern' },
    { id: 3, src: slab3, title: 'Luxury Finish' },
    // Add more when you have them
    // { id: 4, src: slab4, title: 'Natural Beauty' },
    // { id: 5, src: slab5, title: 'Timeless Elegance' },
    // { id: 6, src: slab6, title: 'Sophisticated Design' }
  ];

  useEffect(() => {
    setIsLoaded(true);

    // Prevent body scroll when gallery is open
    if (showGallery) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showGallery]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePosition({ x, y });
    };

    // Intersection Observer for animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            if (entry.target.id === 'specs-section') {
              setSpecsInView(true);
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach((el) => observer.observe(el));

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);

    // Auto-rotate finishes
    const finishInterval = setInterval(() => {
      setActiveFinish((prev) => (prev + 1) % 6);
    }, 3000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      clearInterval(finishInterval);
      observer.disconnect();
    };
  }, []);

  const finishes = [
    { 
      name: 'HONED', 
      description: 'Smooth matte surface with subtle elegance',
      image: quartzite1
    },
    { 
      name: 'POLISHED', 
      description: 'Mirror-like finish showcasing natural beauty',
      image: quartzite2
    },
    { 
      name: 'LEATHER', 
      description: 'Sophisticated textured finish',
      image: quartzite3
    },
    { 
      name: 'FLAMED', 
      description: 'Textured surface with thermal treatment',
      image: quartzite4
    },
    { 
      name: 'SANDBLAST', 
      description: 'Fine granular texture',
      image: quartzite5
    },
    { 
      name: 'RIVER', 
      description: 'Natural water-washed appearance with veining',
      image: quartzite6
    }
  ];

  return (
    <div className={`home-container ${isLoaded ? 'loaded' : ''}`}>
      {/* Hero Section with Parallax */}
      <section className="hero-section" ref={heroRef}>
        <div className="hero-background">
          <div 
            className="parallax-layer layer-1" 
            style={{
              transform: `translateY(${scrollY * 0.5}px)`
            }}
          />
          <div 
            className="parallax-layer layer-2" 
            style={{
              transform: `translateY(${scrollY * 0.3}px)`
            }}
          />
          <div 
            className="parallax-layer layer-3" 
            style={{
              transform: `translateY(${scrollY * 0.1}px)`
            }}
          />
        </div>

        <div className="hero-content">
          <div 
            className="hero-text-wrapper"
            style={{
              transform: `translateY(${scrollY * 0.2}px)`,
              opacity: 1 - scrollY / 800
            }}
          >
            <h1 className="hero-title">
              <span className="title-line-1">{t.introTitle1}</span>
              <span className="title-line-211">{t.introTitle2}</span>
            </h1>
            <div className="hero-subtitle">
              <span>{t.subtitle}</span>
            </div>
          </div>

          <div 
            className="floating-element element-1"
            style={{
              transform: `translate(${mousePosition.x * 20}px, ${mousePosition.y * 20}px)`
            }}
          />
          <div 
            className="floating-element element-2"
            style={{
              transform: `translate(${mousePosition.x * -15}px, ${mousePosition.y * -15}px)`
            }}
          />
          <div 
            className="floating-element element-3"
            style={{
              transform: `translate(${mousePosition.x * 10}px, ${mousePosition.y * -10}px)`
            }}
          />
        </div>

        <div className="scroll-indicator1">
          <div className="scroll-text1">{t.scrollText}</div>
          <div className="scroll-line1"></div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="who-we-are animate-on-scroll">
        <div className="section-container">
          <div className="section-header">
            <span className="section-label">{t.whoWeAre}</span>
            <h2 className="section-title1">{t.ownQuarry}</h2>
          </div>

          <div className="content-grid">
            <div className="text-content">
              <div className="info-block">
                <h3>{t.theRock}</h3>
                <p>{t.theRockText}</p>
              </div>

              <div className="info-block">
                <h3>{t.colorTexture}</h3>
                <p>{t.colorTextureText}</p>
              </div>

              <div className="info-block">
                <h3>{t.application}</h3>
                <p>{t.applicationText}</p>
              </div>
            </div>

            <div className="visual-content">
              <div className="image-showcase">
                <div 
                  className="showcase-image main-image"
                  style={{
                    transform: `translateY(${-scrollY * 0.05}px)`
                  }}
                >
                  <img src={elevatorLobby} alt="Luxury Elevator Lobby" />
                </div>
                <div 
                  className="showcase-image accent-image"
                  style={{
                    transform: `translateY(${-scrollY * 0.08}px)`
                  }}
                >
                  <img src={quarryBlock} alt="Natural Quarry Block" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specifications with Animation */}
      <section className="specs-section animate-on-scroll" id="specs-section" ref={specsRef}>
        <div className="specs-container">
          <div className="specs-header">
            <span className="section-label">{t.performance}</span>
            <h2 className="section-title1">{t.technicalExcellence}</h2>
          </div>

          <div className="specs-grid">
            {specs.map((spec, index) => (
              <div 
                key={index} 
                className="spec-card"
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              >
                <div className="spec-value">
                  <span className={`number ${specsInView ? 'animate' : ''}`}>
                    {spec.value}
                  </span>
                  <span className="unit">{spec.unit}</span>
                </div>
                <div className="spec-label">{spec.label}</div>
                <div className="spec-progress">
                  <div 
                    className="progress-bar"
                    style={{
                      width: specsInView ? '100%' : '0%',
                      transitionDelay: `${index * 0.1}s`
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="offer-section animate-on-scroll">
        <div className="section-container">
          <div className="section-header">
            <span className="section-label">{t.whatWeOffer}</span>
            <h2 className="section-title1">{t.oneStone}</h2>
          </div>

          <div className="offer-description">
            <p>{t.offerDescription}</p>
          </div>

          <div className="finishes-showcase">
            <h3 className="finishes-title">{t.explorefinishes}</h3>
            
            <div className="finishes-display">
              <div className="finish-preview">
                <div className="preview-image">
                  <img 
                    src={finishes[activeFinish].image} 
                    alt={finishes[activeFinish].name}
                    className="finish-img"
                  />
                </div>
                <div className="preview-info">
                  <h4>{finishes[activeFinish].name}</h4>
                  <p>{finishes[activeFinish].description}</p>
                </div>
              </div>

              <div className="finishes-list">
                {finishes.map((finish, index) => (
                  <div 
                    key={index}
                    className={`finish-item ${index === activeFinish ? 'active' : ''}`}
                    onClick={() => setActiveFinish(index)}
                    onMouseEnter={() => setActiveFinish(index)}
                  >
                    <span className="finish-number">0{index + 1}</span>
                    <span className="finish-name">{finish.name}</span>
                    <div className="finish-indicator" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Crafted Section */}
      <section className="crafted-section animate-on-scroll">
        <div className="section-container">
          <div className="crafted-content">
            <div className="crafted-text">
              <span className="section-label">{t.excellence}</span>
              <h2 className="crafted-title">{t.crafted}<br/>{t.defined}</h2>
              <p className="crafted-subtitle">{t.discover}</p>
              
              <div className="product-highlight">
                <h3>{t.quartziteBorghese}</h3>
                <p className="tagline">{t.surfaceStories}</p>
                <button className="cta-button" onClick={() => setShowGallery(true)}>
                  <span>{t.viewGallery}</span>
                  <svg className="button-arrow" viewBox="0 0 24 24">
                    <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="crafted-visual">
              <div className="visual-grid">
                <div className="grid-item item-1">
                  <img src={interior1} alt="Luxury Interior Design" />
                </div>
                <div className="grid-item item-2">
                  <img src={interior2} alt="Modern Restaurant Design" />
                </div>
                <div className="grid-item item-3">
                  <img src={interior3} alt="Elegant Lounge Design" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Slab Gallery Modal */}
      {showGallery && (
        <div className="gallery-modal">
          <div className="gallery-overlay" onClick={() => setShowGallery(false)} />
          <div className="gallery-container">
            <button className="gallery-close" onClick={() => setShowGallery(false)}>
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            
            <div className="gallery-content">
              <div className="gallery-header">
                <h2>Premium Slab Collection</h2>
                <p>Discover the natural beauty of our quartzite</p>
              </div>
              
              <div className="gallery-grid">
                {slabGalleryImages.map((image, index) => (
                  <div 
                    key={image.id} 
                    className={`gallery-item ${selectedImage === index ? 'selected' : ''}`}
                    onClick={() => setSelectedImage(index)}
                    style={{
                      animationDelay: `${index * 0.1}s`
                    }}
                  >
                    <div className="gallery-image-wrapper">
                      <img src={image.src} alt={image.title} />
                      <div className="gallery-image-overlay">
                        <h3>{image.title}</h3>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="gallery-preview">
                <img 
                  src={slabGalleryImages[selectedImage].src} 
                  alt={slabGalleryImages[selectedImage].title}
                  className="preview-main"
                />
                <div className="preview-info">
                  <h3>{slabGalleryImages[selectedImage].title}</h3>
                  <p>Premium Quality • Natural Beige Quartzite</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;